import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, Bell, BellOff, BookOpen, ChevronLeft, FileText, Gavel, Image as ImageIcon, MessageSquarePlus, Paperclip, RefreshCcw, Scale, Search, Trash2, Upload, Vote } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { JudgeArgument, JudgeArgumentAttachment, JudgeCase, JudgeDecision, JudgeProposal, JudgeRegulation } from '../types';

type Tab = 'regulation' | 'cases' | 'proposals';
type CaseFilter = 'open' | 'decision' | 'closed';
const statusLabel: Record<JudgeCase['status'], string> = {
  discussion: 'Discussione aperta', judging: 'Giudice in corso', decided: 'Responso emesso', appeal: 'Ricorso aperto', closed: 'Archiviata'
};

const MAX_APPEALS = 5;
const MAX_ROUND = MAX_APPEALS + 1;
const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'text/plain',
]);

function safeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function attachmentIcon(mimeType: string) {
  return mimeType.startsWith('image/') ? <ImageIcon size={16} /> : <FileText size={16} />;
}

export default function JudgePage() {
  const { user, profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const [tab, setTab] = useState<Tab>('regulation');
  const [regulation, setRegulation] = useState<JudgeRegulation | null>(null);
  const [cases, setCases] = useState<JudgeCase[]>([]);
  const [proposals, setProposals] = useState<JudgeProposal[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [argumentsList, setArgumentsList] = useState<JudgeArgument[]>([]);
  const [decisions, setDecisions] = useState<JudgeDecision[]>([]);
  const [notifyJudge, setNotifyJudge] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [caseFilter, setCaseFilter] = useState<CaseFilter>('open');
  const [caseSearch, setCaseSearch] = useState('');
  const [visibleCaseCount, setVisibleCaseCount] = useState(20);
  const selectedCase = useMemo(() => cases.find((item) => item.id === selectedCaseId) || null, [cases, selectedCaseId]);

  const caseCounts = useMemo(() => ({
    open: cases.filter((item) => ['discussion', 'appeal'].includes(item.status)).length,
    decision: cases.filter((item) => ['judging', 'decided'].includes(item.status)).length,
    closed: cases.filter((item) => item.status === 'closed').length,
  }), [cases]);

  const filteredCases = useMemo(() => {
    const normalizedSearch = caseSearch.trim().toLocaleLowerCase('it-IT');
    return cases.filter((item) => {
      const matchesFilter = caseFilter === 'open'
        ? ['discussion', 'appeal'].includes(item.status)
        : caseFilter === 'decision'
          ? ['judging', 'decided'].includes(item.status)
          : item.status === 'closed';
      const matchesSearch = !normalizedSearch
        || item.title.toLocaleLowerCase('it-IT').includes(normalizedSearch)
        || item.description.toLocaleLowerCase('it-IT').includes(normalizedSearch);
      return matchesFilter && matchesSearch;
    });
  }, [cases, caseFilter, caseSearch]);

  const visibleCases = filteredCases.slice(0, visibleCaseCount);

  function openCase(caseId: string) {
    setSelectedCaseId(caseId);
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  const load = useCallback(async () => {
    const [reg, courtCases, props, settings] = await Promise.all([
      supabase.from('judge_regulations').select('*').eq('is_current', true).maybeSingle(),
      supabase.from('judge_cases').select('*, creator:profiles!judge_cases_created_by_fkey(display_name,nickname)').order('created_at', { ascending: false }),
      supabase.from('judge_proposals').select('*').order('created_at', { ascending: false }),
      user ? supabase.from('notification_settings').select('notify_judge').eq('user_id', user.id).single() : Promise.resolve({ data: null, error: null })
    ]);
    if (reg.error) console.error(reg.error);
    if (courtCases.error) console.error(courtCases.error);
    if (props.error) console.error(props.error);
    setRegulation(reg.data as JudgeRegulation | null);
    setCases((courtCases.data || []) as JudgeCase[]);
    setProposals((props.data || []) as JudgeProposal[]);
    if (settings.data) setNotifyJudge(Boolean(settings.data.notify_judge));
  }, [user]);

  const loadCase = useCallback(async (caseId: string) => {
    const [args, decs] = await Promise.all([
      supabase
        .from('judge_arguments')
        .select('*, profiles(display_name,nickname), judge_argument_attachments(*)')
        .eq('case_id', caseId)
        .order('created_at'),
      supabase.from('judge_decisions').select('*').eq('case_id', caseId).order('round_number')
    ]);

    const loadedArguments = (args.data || []) as JudgeArgument[];
    const attachments = loadedArguments.flatMap(
      (argument) => argument.judge_argument_attachments || [],
    );

    if (attachments.length > 0) {
      const { data: signedFiles, error: signedError } = await supabase.storage
        .from('judge-evidence')
        .createSignedUrls(attachments.map((attachment) => attachment.file_path), 3600);

      if (signedError) {
        console.error(signedError);
      } else {
        const signedUrlByPath = new Map(
          (signedFiles || []).map((item) => [item.path, item.signedUrl]),
        );

        for (const argument of loadedArguments) {
          argument.judge_argument_attachments = (
            argument.judge_argument_attachments || []
          ).map((attachment) => ({
            ...attachment,
            signed_url: signedUrlByPath.get(attachment.file_path) || null,
          }));
        }
      }
    }

    setArgumentsList(loadedArguments);
    setDecisions((decs.data || []) as JudgeDecision[]);
  }, []);

  useEffect(() => { load().catch(console.error); }, [load]);
  useEffect(() => { setVisibleCaseCount(20); }, [caseFilter, caseSearch]);
  useEffect(() => { if (selectedCaseId) loadCase(selectedCaseId).catch(console.error); }, [selectedCaseId, loadCase]);
  useEffect(() => {
    const channel = supabase.channel('judge-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'judge_cases' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'judge_arguments' }, () => selectedCaseId && loadCase(selectedCaseId))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'judge_decisions' }, () => selectedCaseId && loadCase(selectedCaseId))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'judge_argument_attachments' }, () => selectedCaseId && loadCase(selectedCaseId))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'judge_proposals' }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load, loadCase, selectedCaseId]);

  async function toggleNotifications() {
    if (!user) return;
    const next = !notifyJudge;
    setNotifyJudge(next);
    const { error } = await supabase.from('notification_settings').update({ notify_judge: next }).eq('user_id', user.id);
    if (error) { setNotifyJudge(!next); setMessage(error.message); }
  }

  async function uploadRegulation(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || !isAdmin) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const file = form.get('file') as File;
    const text = String(form.get('text') || '').trim();
    const version = String(form.get('version') || '').trim();
    if (!file?.size || text.length < 20) return setMessage('Seleziona il file e incolla il testo completo del regolamento.');
    setBusy(true); setMessage(null);
    try {
      const path = `current/${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: uploadError } = await supabase.storage.from('judge-regulations').upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) throw uploadError;
      const oldPath = regulation?.file_path;
      if (regulation) await supabase.from('judge_regulations').update({ is_current: false }).eq('id', regulation.id);
      const { error } = await supabase.from('judge_regulations').insert({ file_path: path, file_name: file.name, mime_type: file.type || 'application/pdf', regulation_text: text, version_label: version || null, uploaded_by: user.id });
      if (error) throw error;
      if (oldPath) await supabase.storage.from('judge-regulations').remove([oldPath]);
      formElement.reset();
      await load();
      setMessage('Regolamento aggiornato. I campi sono stati svuotati e il file precedente è stato eliminato.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Errore durante il caricamento'); }
    finally { setBusy(false); }
  }

  async function createCase(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const title = String(form.get('title') || '').trim();
    const description = String(form.get('description') || '').trim();

    setBusy(true);
    setMessage(null);

    try {
      const { data, error } = await supabase
        .from('judge_cases')
        .insert({ title, description, created_by: user.id })
        .select('*, creator:profiles!judge_cases_created_by_fkey(display_name,nickname)')
        .single();

      if (error) throw error;

      formElement.reset();
      setCases((current) => [data as JudgeCase, ...current.filter((item) => item.id !== data.id)]);
      setSelectedCaseId(data.id);
      setTab('cases');
      await load();
      setMessage('Sentenza pubblicata. I campi sono stati svuotati.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossibile creare la sentenza.');
    } finally {
      setBusy(false);
    }
  }

  async function addArgument(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || !selectedCase) return;

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const body = String(form.get('body') || '').trim();
    const imageInput = formElement.elements.namedItem('attachment_images');
    const documentInput = formElement.elements.namedItem('attachment_documents');
    const imageFiles = imageInput instanceof HTMLInputElement
      ? Array.from(imageInput.files || [])
      : [];
    const documentFiles = documentInput instanceof HTMLInputElement
      ? Array.from(documentInput.files || [])
      : [];
    const files = [...imageFiles, ...documentFiles];

    if (files.length > MAX_ATTACHMENTS) {
      setMessage(`Puoi allegare al massimo ${MAX_ATTACHMENTS} file.`);
      return;
    }

    const invalidFile = files.find(
      (file) => !ALLOWED_ATTACHMENT_TYPES.has(file.type) || file.size > MAX_FILE_BYTES,
    );
    if (invalidFile) {
      setMessage(`File non valido: ${invalidFile.name}. Sono ammessi JPG, PNG, WEBP, PDF e TXT fino a 5 MB ciascuno.`);
      return;
    }

    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (totalBytes > MAX_TOTAL_BYTES) {
      setMessage('Gli allegati possono pesare al massimo 10 MB complessivi per intervento.');
      return;
    }

    const argumentId = crypto.randomUUID();
    const uploadedPaths: string[] = [];

    setBusy(true);
    setMessage(null);

    try {
      const attachmentsToInsert: Omit<JudgeArgumentAttachment, 'created_at' | 'signed_url'>[] = [];

      for (const file of files) {
        const filePath = `${user.id}/${selectedCase.id}/${argumentId}/${crypto.randomUUID()}-${safeFileName(file.name)}`;
        const { error: uploadError } = await supabase.storage
          .from('judge-evidence')
          .upload(filePath, file, { contentType: file.type, upsert: false });

        if (uploadError) throw uploadError;
        uploadedPaths.push(filePath);
        attachmentsToInsert.push({
          id: crypto.randomUUID(),
          argument_id: argumentId,
          case_id: selectedCase.id,
          round_number: selectedCase.current_round,
          user_id: user.id,
          file_path: filePath,
          file_name: file.name,
          mime_type: file.type,
          size_bytes: file.size,
        });
      }

      const { error: argumentError } = await supabase.from('judge_arguments').insert({
        id: argumentId,
        case_id: selectedCase.id,
        round_number: selectedCase.current_round,
        user_id: user.id,
        body,
      });
      if (argumentError) throw argumentError;

      if (attachmentsToInsert.length > 0) {
        const { error: attachmentError } = await supabase
          .from('judge_argument_attachments')
          .insert(attachmentsToInsert);
        if (attachmentError) throw attachmentError;
      }

      formElement.reset();
      await loadCase(selectedCase.id);
      setMessage(
        files.length > 0
          ? `Intervento pubblicato con ${files.length} allegat${files.length === 1 ? 'o' : 'i'}.`
          : 'Intervento pubblicato. Il campo è stato svuotato.',
      );
    } catch (error) {
      if (uploadedPaths.length > 0) {
        await supabase.storage.from('judge-evidence').remove(uploadedPaths);
      }
      setMessage(error instanceof Error ? error.message : 'Impossibile pubblicare l’intervento.');
    } finally {
      setBusy(false);
    }
  }

  async function adminCaseAction(action: 'start' | 'appeal' | 'close') {
    if (!selectedCase || !isAdmin) return;
    setBusy(true); setMessage(null);
    try {
      if (action === 'start') {
        const { data, error } = await supabase.functions.invoke('judge-ai', { body: { action: 'case', caseId: selectedCase.id } });
        if (error) throw error; if (data?.error) throw new Error(data.error);
      } else if (action === 'appeal') {
        if (selectedCase.current_round >= MAX_ROUND) {
          throw new Error('Sono già stati utilizzati i cinque ricorsi disponibili.');
        }
        const { error } = await supabase.from('judge_cases').update({ status: 'appeal', current_round: selectedCase.current_round + 1 }).eq('id', selectedCase.id);
        if (error) throw error;
      } else {
        const confirmed = window.confirm(
          'Vuoi chiudere definitivamente questa sentenza?\n\nTutte le fotografie, le immagini e i documenti allegati in ogni grado verranno eliminati definitivamente. Testi, interventi e responsi resteranno consultabili nell’archivio.',
        );

        if (!confirmed) return;

        const { data: attachmentRows, error: attachmentReadError } = await supabase
          .from('judge_argument_attachments')
          .select('id, file_path')
          .eq('case_id', selectedCase.id);
        if (attachmentReadError) throw attachmentReadError;

        const evidencePaths = (attachmentRows || []).map((item) => item.file_path);

        if (evidencePaths.length > 0) {
          const { error: storageError } = await supabase.storage
            .from('judge-evidence')
            .remove(evidencePaths);
          if (storageError) throw new Error(`Impossibile eliminare gli allegati: ${storageError.message}`);

          const { error: attachmentDeleteError } = await supabase
            .from('judge_argument_attachments')
            .delete()
            .eq('case_id', selectedCase.id);
          if (attachmentDeleteError) throw new Error(`Allegati rimossi dallo Storage, ma errore nella pulizia del database: ${attachmentDeleteError.message}`);
        }

        const { error } = await supabase
          .from('judge_cases')
          .update({ status: 'closed', closed_at: new Date().toISOString() })
          .eq('id', selectedCase.id);
        if (error) throw error;

        setMessage(
          evidencePaths.length > 0
            ? `Sentenza chiusa. Eliminati definitivamente ${evidencePaths.length} allegat${evidencePaths.length === 1 ? 'o' : 'i'}.`
            : 'Sentenza chiusa. Non erano presenti allegati da eliminare.',
        );
      }
      await load(); await loadCase(selectedCase.id);
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Operazione non riuscita'); }
    finally { setBusy(false); }
  }

  async function deleteCase(caseId: string, title: string) {
    if (!isAdmin) return;

    const confirmed = window.confirm(
      `Vuoi davvero eliminare definitivamente la sentenza “${title}”?\n\nVerranno eliminati anche tutti gli interventi, i responsi e i ricorsi collegati. L’operazione non può essere annullata.`,
    );

    if (!confirmed) return;

    setBusy(true);
    setMessage(null);

    try {
      const { data: attachmentRows, error: attachmentReadError } = await supabase
        .from('judge_argument_attachments')
        .select('file_path')
        .eq('case_id', caseId);
      if (attachmentReadError) throw attachmentReadError;

      const { error } = await supabase.from('judge_cases').delete().eq('id', caseId);
      if (error) throw error;

      const evidencePaths = (attachmentRows || []).map((item) => item.file_path);
      if (evidencePaths.length > 0) {
        const { error: storageError } = await supabase.storage
          .from('judge-evidence')
          .remove(evidencePaths);
        if (storageError) console.error(storageError);
      }

      if (selectedCaseId === caseId) {
        setSelectedCaseId(null);
        setArgumentsList([]);
        setDecisions([]);
      }

      setCases((current) => current.filter((item) => item.id !== caseId));
      await load();
      setMessage('Sentenza eliminata definitivamente.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossibile eliminare la sentenza.');
    } finally {
      setBusy(false);
    }
  }

  async function deleteProposal(proposalId: string, title: string) {
    if (!isAdmin) return;

    const confirmed = window.confirm(
      `Vuoi davvero eliminare definitivamente la proposta “${title}”?\n\nL’eventuale valutazione dell’IA verrà eliminata. L’operazione non può essere annullata.`,
    );

    if (!confirmed) return;

    setBusy(true);
    setMessage(null);

    try {
      const { error } = await supabase.from('judge_proposals').delete().eq('id', proposalId);
      if (error) throw error;

      setProposals((current) => current.filter((item) => item.id !== proposalId));
      await load();
      setMessage('Proposta eliminata definitivamente.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossibile eliminare la proposta.');
    } finally {
      setBusy(false);
    }
  }

  async function createProposal(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const title = String(form.get('title') || '').trim();
    const proposalText = String(form.get('text') || '').trim();

    setBusy(true);
    setMessage(null);

    try {
      const { data, error } = await supabase
        .from('judge_proposals')
        .insert({ title, proposal_text: proposalText, created_by: user.id })
        .select('*')
        .single();

      if (error) throw error;

      formElement.reset();
      setProposals((current) => [data as JudgeProposal, ...current.filter((item) => item.id !== data.id)]);
      await load();
      setMessage('Proposta salvata e mostrata nell’elenco. I campi sono stati svuotati.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossibile salvare la proposta.');
    } finally {
      setBusy(false);
    }
  }

  async function evaluateProposal(id: string) {
    setBusy(true); setMessage(null);
    const { data, error } = await supabase.functions.invoke('judge-ai', { body: { action: 'proposal', proposalId: id } });
    if (error || data?.error) setMessage(data?.error || error?.message || 'Errore');
    await load(); setBusy(false);
  }

  const regulationUrl = regulation ? supabase.storage.from('judge-regulations').getPublicUrl(regulation.file_path).data.publicUrl : null;

  return <div className="page-stack judge-page">
    <section className="hero-card judge-hero">
      <div><span className="eyebrow">Regole e controversie</span><h1>Giudice</h1><p className="muted">Discussioni pubbliche, responsi motivati sul regolamento e ricorsi tracciati.</p></div>
      <button className="ghost-button" onClick={toggleNotifications}>{notifyJudge ? <Bell size={18}/> : <BellOff size={18}/>} {notifyJudge ? 'Silenzia Giudice' : 'Riattiva notifiche'}</button>
    </section>
    {message && <section className="panel"><p className="muted">{message}</p></section>}
    <div className="segmented judge-tabs">
      <button className={tab === 'regulation' ? 'active' : ''} onClick={() => setTab('regulation')}><BookOpen size={17}/> Regolamento</button>
      <button className={tab === 'cases' ? 'active' : ''} onClick={() => setTab('cases')}><Scale size={17}/> Sentenze</button>
      <button className={tab === 'proposals' ? 'active' : ''} onClick={() => setTab('proposals')}><Vote size={17}/> Proposte</button>
    </div>

    {tab === 'regulation' && <>
      <section className="panel">
        <div className="panel-header"><h2><BookOpen size={20}/> Regolamento vigente</h2>{regulationUrl && <a className="primary-button soft" href={regulationUrl} target="_blank" rel="noreferrer">Apri file</a>}</div>
        {regulation ? <><h3>{regulation.title}</h3><p className="muted">{regulation.version_label || regulation.file_name} · aggiornato il {new Date(regulation.created_at).toLocaleDateString('it-IT')}</p></> : <p className="muted">Nessun regolamento caricato.</p>}
      </section>
      {isAdmin && <form className="panel form-stack" onSubmit={uploadRegulation}>
        <h2><Upload size={20}/> Aggiorna regolamento</h2>
        <label>Versione o data<input name="version" placeholder="Es. Stagione 2026/27" /></label>
        <label>File PDF o TXT<input name="file" type="file" accept="application/pdf,text/plain" required /></label>
        <label>Testo completo per il Giudice<textarea name="text" rows={14} placeholder="Incolla qui il testo del regolamento: serve all’IA per citarlo e applicarlo correttamente." required /></label>
        <button type="submit" className="primary-button" disabled={busy}><Upload size={18}/> Salva e sostituisci</button>
      </form>}
    </>}

    {tab === 'cases' && <div className={`judge-columns ${selectedCase ? 'has-selected-case' : ''}`}>
      <div className="page-stack judge-case-list-pane">
        <form className="panel form-stack" onSubmit={createCase}><h2><MessageSquarePlus size={20}/> Apri una sentenza</h2><label>Titolo<input name="title" minLength={3} required /></label><label>Descrizione del caso<textarea name="description" rows={6} minLength={10} required /></label><button type="submit" className="primary-button" disabled={busy}>Pubblica il caso</button></form>
        <section className="panel judge-case-browser">
          <div className="panel-header"><div><h2>Sentenze</h2><p className="muted">Apri un caso per leggerlo e partecipare.</p></div></div>
          <div className="judge-case-filters" role="tablist" aria-label="Filtra sentenze">
            <button type="button" className={caseFilter === 'open' ? 'active' : ''} onClick={() => setCaseFilter('open')}>Aperte <span>{caseCounts.open}</span></button>
            <button type="button" className={caseFilter === 'decision' ? 'active' : ''} onClick={() => setCaseFilter('decision')}>In decisione <span>{caseCounts.decision}</span></button>
            <button type="button" className={caseFilter === 'closed' ? 'active' : ''} onClick={() => setCaseFilter('closed')}>Archiviate <span>{caseCounts.closed}</span></button>
          </div>
          <label className="judge-case-search"><Search size={17}/><input value={caseSearch} onChange={(event) => setCaseSearch(event.target.value)} placeholder="Cerca per titolo o descrizione" /></label>
          <div className="judge-list">
            {visibleCases.map((item) => <div key={item.id} className={`judge-list-row ${item.id === selectedCaseId ? 'selected' : ''}`}><button type="button" className="judge-list-item" onClick={() => openCase(item.id)}><strong>{item.title}</strong><span>{statusLabel[item.status]} · grado {item.current_round}</span></button>{isAdmin && <button type="button" className="danger-button judge-delete-button" disabled={busy} onClick={() => deleteCase(item.id, item.title)} aria-label={`Cancella la sentenza ${item.title}`} title="Cancella sentenza"><Trash2 size={17}/> Cancella</button>}</div>)}
            {filteredCases.length === 0 && <p className="muted judge-empty-list">Nessuna sentenza in questa sezione.</p>}
          </div>
          {visibleCaseCount < filteredCases.length && <button type="button" className="ghost-button judge-load-more" onClick={() => setVisibleCaseCount((count) => count + 20)}>Carica altre sentenze</button>}
        </section>
      </div>
      <section className="panel judge-detail">
        {!selectedCase ? <p className="muted">Seleziona una sentenza per leggere e partecipare.</p> : <>
          <button type="button" className="ghost-button judge-mobile-back" onClick={() => setSelectedCaseId(null)}><ChevronLeft size={18}/> Torna alle sentenze</button>
          <div className="panel-header"><div><span className={`judge-status ${selectedCase.status}`}>{statusLabel[selectedCase.status]}</span><h2>{selectedCase.title}</h2></div></div>
          <p>{selectedCase.description}</p>
          {Array.from({ length: selectedCase.current_round }, (_, i) => i + 1).map((round) => <div className="judge-round" key={round}><h3>{round === 1 ? 'Primo giudizio' : `Ricorso ${round - 1}`}</h3>{argumentsList.filter((a) => a.round_number === round).map((arg) => <article className="judge-argument" key={arg.id}><strong>{arg.profiles?.display_name || arg.profiles?.nickname || 'Utente'}</strong><p>{arg.body}</p>{(arg.judge_argument_attachments || []).length > 0 && <div className="judge-attachments">{(arg.judge_argument_attachments || []).map((attachment) => attachment.signed_url ? <a key={attachment.id} href={attachment.signed_url} target="_blank" rel="noreferrer" className="judge-attachment-link">{attachmentIcon(attachment.mime_type)}<span>{attachment.file_name}</span><small>{Math.ceil(attachment.size_bytes / 1024)} KB</small></a> : <span key={attachment.id} className="judge-attachment-link disabled">{attachmentIcon(attachment.mime_type)}<span>{attachment.file_name}</span></span>)}</div>}<small>{new Date(arg.created_at).toLocaleString('it-IT')}</small></article>)}{decisions.filter((d) => d.round_number === round).map((d) => <article className="judge-decision" key={d.id}><h4><Gavel size={18}/> Responso del Giudice</h4><p>{d.reasoning}</p><strong>Decisione: {d.verdict}</strong><small>Affidabilità dichiarata: {d.confidence || 'non indicata'} · {d.model}</small></article>)}</div>)}
          {['discussion','appeal'].includes(selectedCase.status) && <form className="form-stack judge-comment-form" onSubmit={addArgument}><label>Il tuo punto di vista<textarea name="body" rows={5} minLength={2} required /></label><div className="judge-upload-grid"><label><span><ImageIcon size={17}/> Foto e immagini</span><input name="attachment_images" type="file" multiple accept="image/jpeg,image/png,image/webp" /><small className="muted">Puoi scegliere più immagini.</small></label><label><span><FileText size={17}/> Documenti</span><input name="attachment_documents" type="file" multiple accept="application/pdf,text/plain,.pdf,.txt" /><small className="muted">Puoi aggiungere PDF e TXT insieme alle immagini.</small></label></div><small className="muted">Limite complessivo: massimo 5 allegati, 5 MB ciascuno e 10 MB totali.</small><button type="submit" className="primary-button soft" disabled={busy}><Paperclip size={17}/> Pubblica intervento</button></form>}
          {isAdmin && <div className="judge-admin-actions">{['discussion','appeal'].includes(selectedCase.status) && <button type="button" className="primary-button" disabled={busy || !regulation} onClick={() => adminCaseAction('start')}><Gavel size={18}/> Avvia Giudice</button>}{selectedCase.status === 'decided' && <>{selectedCase.current_round < MAX_ROUND ? <button type="button" className="ghost-button" disabled={busy} onClick={() => adminCaseAction('appeal')}><RefreshCcw size={18}/> Fai ricorso</button> : <p className="muted">Sono già stati utilizzati i cinque ricorsi disponibili. La sentenza può soltanto essere chiusa.</p>}<button type="button" className="danger-button" disabled={busy} onClick={() => adminCaseAction('close')}><Archive size={18}/> Chiudi sentenza</button></>}</div>}
        </>}
      </section>
    </div>}

    {tab === 'proposals' && <div className="page-stack">
      <form className="panel form-stack" onSubmit={createProposal}><h2><Vote size={20}/> Nuova proposta</h2><label>Titolo<input name="title" minLength={3} required /></label><label>Testo della proposta<textarea name="text" rows={6} minLength={10} required /></label><button type="submit" className="primary-button" disabled={busy}>Salva proposta</button></form>
      <section className="panel"><h2>Valutazioni</h2><div className="judge-proposals">{proposals.map((p) => <article className="judge-proposal" key={p.id}><div><h3>{p.title}</h3><p>{p.proposal_text}</p>{p.ai_reasoning && <div className="judge-decision"><p>{p.ai_reasoning}</p><strong>{p.ai_verdict === 'eligible' ? 'Può andare in votazione' : 'Non può andare in votazione'}</strong></div>}</div>{isAdmin && <div className="judge-proposal-actions">{p.status === 'draft' && <button type="button" className="primary-button soft" disabled={busy || !regulation} onClick={() => evaluateProposal(p.id)}>Valuta con IA</button>}<button type="button" className="danger-button" disabled={busy} onClick={() => deleteProposal(p.id, p.title)}><Trash2 size={17}/> Cancella</button></div>}</article>)}</div></section>
    </div>}
  </div>;
}
